/**
 * Database Assistant Page
 * AI-powered natural language database query interface
 */

import React, { useState, useEffect, useRef } from 'react';
import { useDatabaseAssistant } from '../hooks/useDatabaseAssistant';
import { FaPaperPlane, FaStar, FaRegStar, FaCopy, FaDownload, FaLightbulb, FaHistory, FaDatabase, FaChevronDown, FaChevronUp, FaChevronRight, FaTrash, FaRobot, FaUser } from 'react-icons/fa';
import styles from './DatabaseAssistant.module.css';

function DatabaseAssistant() {
    const [question, setQuestion] = useState('');
    const [chatMessages, setChatMessages] = useState([]);
    const [showExamples, setShowExamples] = useState(true);
    const [showHistory, setShowHistory] = useState(false);
    const [showFavorites, setShowFavorites] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [showSidebar, setShowSidebar] = useState(false);
    const textareaRef = useRef(null);
    const chatEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const processedResponseRef = useRef(null);

    const {
        response,
        examples,
        history,
        favorites,
        loading,
        sessionId,
        awaitingConfirmation,
        askQuestion,
        fetchExamples,
        exportToCSV,
        copyToClipboard,
        addToFavorites,
        removeFromFavorites,
        clearHistory,
        clearFavorites,
        clearConversation,
        handleConfirm,
        handleDecline
    } = useDatabaseAssistant();

    // Load examples on mount
    useEffect(() => {
        fetchExamples();
    }, [fetchExamples]);

    // Auto-focus textarea on mount
    useEffect(() => {
        textareaRef.current?.focus();
    }, []);

    // Scroll to bottom when new message arrives
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages]);

    // Add response to chat messages
    useEffect(() => {
        if (response && response !== processedResponseRef.current) {
            processedResponseRef.current = response;
            
            // Update the last message with the response
            setChatMessages(prev => {
                const updated = [...prev];
                if (updated.length > 0) {
                    updated[updated.length - 1].response = response;
                }
                return updated;
            });
        }
    }, [response]);

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!question.trim() || loading || awaitingConfirmation) return;

        // Clear input immediately
        const userQuestion = question.trim();
        setQuestion('');
        
        // Add user question to chat immediately (before API call)
        const messageId = Date.now() + Math.random();
        setChatMessages(prev => [...prev, { 
            question: userQuestion,
            response: null,  // Will be filled in when response arrives
            timestamp: new Date().toISOString(),
            id: messageId
        }]);
        
        await askQuestion(userQuestion);
    };

    const handleExampleClick = (exampleQuestion) => {
        setQuestion(exampleQuestion);
        textareaRef.current?.focus();
    };

    const handleHistoryClick = (historyItem) => {
        setQuestion(historyItem.question);
        textareaRef.current?.focus();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    const handleConfirmClick = async () => {
        // Add confirmation message to chat
        const messageId = Date.now() + Math.random();
        setChatMessages(prev => [...prev, { 
            question: 'Yes, that\'s correct',
            response: null,
            timestamp: new Date().toISOString(),
            id: messageId
        }]);
        
        await handleConfirm();
    };

    const handleDeclineClick = async () => {
        // Add decline message to chat
        const messageId = Date.now() + Math.random();
        setChatMessages(prev => [...prev, { 
            question: 'No, let me rephrase',
            response: null,
            timestamp: new Date().toISOString(),
            id: messageId
        }]);
        
        await handleDecline();
    };

    const isFavorite = (q) => favorites.includes(q);

    const toggleFavorite = () => {
        if (!question.trim()) return;

        if (isFavorite(question.trim())) {
            removeFromFavorites(question.trim());
        } else {
            addToFavorites(question.trim());
        }
    };

    const clearChat = async () => {
        setChatMessages([]);
        
        // Clear the backend conversation context
        if (sessionId) {
            try {
                await clearConversation();
            } catch (error) {
                // Silently handle JSON parsing errors from backend text responses
                const errorMessage = error instanceof Error ? error.message : '';
                if (!errorMessage.toLowerCase().includes('not valid json')) {
                    console.error('Failed to clear backend conversation:', error);
                }
                // UI is already cleared, so we can continue regardless
            }
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Database Assistant</h1>
            </div>

            <div className={styles.content}>
                {/* Chat Section */}
                <div className={styles.chatSection}>
                    <div className={styles.chatWindow} ref={chatContainerRef}>
                        {chatMessages.length === 0 ? (
                            <div className={styles.welcomeMessage}>
                                <FaRobot className={styles.welcomeIcon} />
                                <h2>Hello! I'm your Database Assistant</h2>
                                <p>Ask questions about your database in natural language. Try: "How many active clients?", "List samples from last week", or "Is Kara Co a customer?"</p>
                                <div className={styles.conversationHint}>
                                    <p><strong>💡 Tip:</strong> I remember our conversation for follow-ups like "Show me the top 5" or "List them"</p>
                                    <p><strong>⚠️ Note:</strong> Read-only access - questions requesting data modification will be blocked</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                {chatMessages.map((msg) => (
                                    <div key={msg.id} className={styles.messageGroup}>
                                        {/* User Question */}
                                        <div className={styles.userMessage}>
                                            <div className={styles.messageContent}>
                                                <div className={styles.messageHeader}>
                                                    <FaUser className={styles.messageIcon} />
                                                    <span className={styles.messageLabel}>You</span>
                                                </div>
                                                <div className={styles.messageText}>
                                                    {msg.question}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Assistant Response */}
                                        {msg.response ? (
                                            <div className={`${styles.assistantMessage} ${msg.response.success ? '' : styles.error}`}>
                                                <div className={styles.messageContent}>
                                                    <div className={styles.messageHeader}>
                                                        <FaRobot className={styles.messageIcon} />
                                                        <span className={styles.messageLabel}>Assistant</span>
                                                        {msg.response.success && msg.response.answer && (
                                                            <button
                                                                onClick={() => copyToClipboard(msg.response.answer, 'Answer')}
                                                                className={styles.copyBtn}
                                                                title="Copy answer"
                                                            >
                                                                <FaCopy />
                                                            </button>
                                                        )}
                                                    </div>
                                                    
                                                    {msg.response.success ? (
                                                        <>
                                                            {/* Only show answer if not awaiting confirmation */}
                                                            {!msg.response.needsConfirmation && msg.response.answer && (
                                                                <div className={styles.messageText}>
                                                                    {msg.response.answer}
                                                                </div>
                                                            )}

                                                            {/* Data Table */}
                                                            {msg.response.queryResult && msg.response.queryResult.rowCount > 0 && (
                                                                <div className={styles.dataTable}>
                                                                    <div className={styles.tableHeader}>
                                                                        <span className={styles.tableTitle}>
                                                                            📊 {msg.response.queryResult.rowCount} results
                                                                        </span>
                                                                        <button
                                                                            onClick={() => exportToCSV(msg.response.queryResult, 'database-query')}
                                                                            className={styles.exportBtn}
                                                                            title="Export to CSV"
                                                                        >
                                                                            <FaDownload /> Export
                                                                        </button>
                                                                    </div>
                                                                    <div className={styles.tableWrapper}>
                                                                        <table>
                                                                            <thead>
                                                                                <tr>
                                                                                    {msg.response.queryResult.columns.map(col => (
                                                                                        <th key={col} title={col}>{col}</th>
                                                                                    ))}
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {msg.response.queryResult.rows.slice(0, 100).map((row, idx) => (
                                                                                    <tr key={idx}>
                                                                                        {msg.response.queryResult.columns.map(col => (
                                                                                            <td key={col} title={String(row[col] ?? '')}>{String(row[col] ?? '')}</td>
                                                                                        ))}
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                    {msg.response.queryResult.columns.length > 5 && (
                                                                        <p className={styles.tableScrollHint}>
                                                                            ← Scroll horizontally to see all columns →
                                                                        </p>
                                                                    )}
                                                                    {msg.response.queryResult.rowCount > 100 && (
                                                                        <p className={styles.tableNote}>
                                                                            Showing first 100 of {msg.response.queryResult.rowCount} rows
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className={styles.errorText}>
                                                            <p>{msg.response.answer || msg.response.error || 'An unknown error occurred'}</p>
                                                            {/* Check error message content for security/forbidden operations */}
                                                            {(msg.response.error?.toLowerCase().includes('forbidden') || 
                                                              msg.response.error?.toLowerCase().includes('not allowed') ||
                                                              msg.response.error?.toLowerCase().includes('read-only') ||
                                                              msg.response.answer?.toLowerCase().includes('security error')) && (
                                                                <div className={styles.hint}>
                                                                    <p><strong>🔒 Security Block</strong></p>
                                                                    <p>Database Assistant is <strong>read-only</strong>. You can:</p>
                                                                    <ul>
                                                                        <li>✅ Query any data (SELECT)</li>
                                                                        <li>✅ Count records and statistics</li>
                                                                        <li>✅ Filter and search</li>
                                                                    </ul>
                                                                    <p>But you cannot:</p>
                                                                    <ul>
                                                                        <li>❌ Modify data (INSERT, UPDATE, DELETE)</li>
                                                                        <li>❌ Change schema (CREATE, ALTER, DROP)</li>
                                                                    </ul>
                                                                </div>
                                                            )}
                                                            {/* Check for unclear/invalid question errors */}
                                                            {(msg.response.error?.toLowerCase().includes('unclear') ||
                                                              msg.response.error?.toLowerCase().includes('rephrase') ||
                                                              msg.response.error?.toLowerCase().includes('understand')) && (
                                                                <div className={styles.hint}>
                                                                    <p><strong>💡 Try rephrasing your question</strong></p>
                                                                    <p>Examples of clear questions:</p>
                                                                    <ul>
                                                                        <li>"How many clients do we have?"</li>
                                                                        <li>"Is [company name] a customer?"</li>
                                                                        <li>"List samples from last week"</li>
                                                                        <li>"Show me the 5 most recent clients"</li>
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Clarification Request */}
                                                    {msg.response.success && msg.response.needsConfirmation && msg.response.clarification && (
                                                        <div className={styles.clarificationBox}>
                                                            <div className={styles.clarificationIcon}>❓</div>
                                                            <p className={styles.clarificationText}>{msg.response.clarification}</p>
                                                            <div className={styles.clarificationActions}>
                                                                <button 
                                                                    onClick={handleConfirmClick}
                                                                    className={styles.confirmBtn}
                                                                    disabled={loading || !awaitingConfirmation}
                                                                >
                                                                    ✓ Yes, that's correct
                                                                </button>
                                                                <button 
                                                                    onClick={handleDeclineClick}
                                                                    className={styles.declineBtn}
                                                                    disabled={loading || !awaitingConfirmation}
                                                                >
                                                                    ✗ No, let me rephrase
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className={styles.assistantMessage}>
                                                <div className={styles.messageContent}>
                                                    <div className={styles.messageHeader}>
                                                        <FaRobot className={styles.messageIcon} />
                                                        <span className={styles.messageLabel}>Assistant</span>
                                                    </div>
                                                    <div className={styles.typingIndicator}>
                                                        <span></span>
                                                        <span></span>
                                                        <span></span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <div ref={chatEndRef} />
                            </>
                        )}
                    </div>

                    {/* Input Area */}
                    <div className={styles.inputArea}>
                        {awaitingConfirmation && (
                            <div className={styles.awaitingConfirmationBanner}>
                                ⏳ Awaiting your confirmation above...
                            </div>
                        )}
                        <form onSubmit={handleSubmit} className={styles.inputForm}>
                            <textarea
                                ref={textareaRef}
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder={awaitingConfirmation ? "Please confirm or decline the clarification above..." : "Ask about clients, contacts, samples, or stats... (e.g., 'How many active clients?')"}
                                className={styles.questionInput}
                                rows={1}
                                disabled={loading || awaitingConfirmation}
                            />
                            <div className={styles.inputButtons}>
                                {chatMessages.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={clearChat}
                                        className={styles.clearChatBtn}
                                        title="Clear conversation"
                                        disabled={loading || awaitingConfirmation}
                                    >
                                        <FaTrash />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={toggleFavorite}
                                    className={styles.favoriteBtn}
                                    disabled={!question.trim() || awaitingConfirmation}
                                    title={isFavorite(question.trim()) ? 'Remove from favorites' : 'Add to favorites'}
                                >
                                    {isFavorite(question.trim()) ? (
                                        <FaStar className={styles.starFilled} />
                                    ) : (
                                        <FaRegStar />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowSidebar(!showSidebar)}
                                    className={styles.examplesBtn}
                                    title="Open suggestions and history"
                                >
                                    <FaLightbulb />
                                </button>
                                <button
                                    type="submit"
                                    className={styles.sendBtn}
                                    disabled={loading || !question.trim() || awaitingConfirmation}
                                >
                                    <FaPaperPlane />
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Sidebar - Examples, History, Favorites */}
                {showSidebar && <div className={styles.sidebarOverlay} onClick={() => setShowSidebar(false)} />}
                <div className={`${styles.sidebar} ${showSidebar ? styles.sidebarOpen : ''}`}>
                    {/* Close Button on Left Edge of Sidebar */}
                    <button 
                        className={styles.sidebarCloseBtn}
                        onClick={() => setShowSidebar(false)}
                        title="Close suggestions and history"
                    >
                        <FaChevronRight />
                    </button>
                    
                    {/* Example Questions */}
                    {examples && (
                        <div className={styles.sidebarSection}>
                            <button
                                onClick={() => setShowExamples(!showExamples)}
                                className={styles.sectionHeader}
                            >
                                <FaLightbulb /> Example Questions
                                {showExamples ? <FaChevronUp /> : <FaChevronDown />}
                            </button>

                            {showExamples && (
                                <div className={styles.sectionContent}>
                                    {Object.entries(examples.categories || {}).map(([category, questions]) => (
                                        <div key={category} className={styles.category}>
                                            <button
                                                onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                                                className={styles.categoryHeader}
                                            >
                                                {category}
                                                {selectedCategory === category ? <FaChevronUp /> : <FaChevronDown />}
                                            </button>
                                            {selectedCategory === category && (
                                                <div className={styles.categoryContent}>
                                                    {questions.map((q, idx) => (
                                                        <button
                                                            key={idx}
                                                            onClick={() => handleExampleClick(q)}
                                                            className={styles.exampleBtn}
                                                        >
                                                            {q}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Query History */}
                    {history.length > 0 && (
                        <div className={styles.sidebarSection}>
                            <button
                                onClick={() => setShowHistory(!showHistory)}
                                className={styles.sectionHeader}
                            >
                                <FaHistory /> Recent Queries ({history.length})
                                {showHistory ? <FaChevronUp /> : <FaChevronDown />}
                            </button>

                            {showHistory && (
                                <div className={styles.sectionContent}>
                                    <div className={styles.historyActions}>
                                        <button
                                            onClick={clearHistory}
                                            className={styles.clearBtn}
                                        >
                                            <FaTrash /> Clear History
                                        </button>
                                    </div>
                                    <div className={styles.historyList}>
                                        {history.map((item, idx) => (
                                            <div key={idx} className={styles.historyItem}>
                                                <button
                                                    onClick={() => handleHistoryClick(item)}
                                                    className={styles.historyQuestion}
                                                >
                                                    <span className={styles.historyText}>{item.question}</span>
                                                    <span className={styles.historyTime}>
                                                        {new Date(item.timestamp).toLocaleString()}
                                                    </span>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Favorites */}
                    {favorites.length > 0 && (
                        <div className={styles.sidebarSection}>
                            <button
                                onClick={() => setShowFavorites(!showFavorites)}
                                className={styles.sectionHeader}
                            >
                                <FaStar /> Favorites ({favorites.length})
                                {showFavorites ? <FaChevronUp /> : <FaChevronDown />}
                            </button>

                            {showFavorites && (
                                <div className={styles.sectionContent}>
                                    <div className={styles.historyActions}>
                                        <button
                                            onClick={clearFavorites}
                                            className={styles.clearBtn}
                                        >
                                            <FaTrash /> Clear Favorites
                                        </button>
                                    </div>
                                    <div className={styles.favoritesList}>
                                        {favorites.map((fav, idx) => (
                                            <div key={idx} className={styles.favoriteItem}>
                                                <button
                                                    onClick={() => handleExampleClick(fav)}
                                                    className={styles.favoriteQuestion}
                                                >
                                                    {fav}
                                                </button>
                                                <button
                                                    onClick={() => removeFromFavorites(fav)}
                                                    className={styles.removeFavoriteBtn}
                                                    title="Remove from favorites"
                                                >
                                                    <FaStar />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DatabaseAssistant;
